import { useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { SupportService, type SupportTicketStatus } from '@/lib/support-service';
import { useAuth } from '@/hooks/use-auth';
import { usePlatformAdmin } from '@/hooks/use-platform-admin';

const SUPPORT_TICKETS_QUERY_KEY = ['support-tickets'] as const;

export const useSupportTickets = () => {
  const queryClient = useQueryClient();
  const { companyId, user } = useAuth();
  const { isPlatformAdmin } = usePlatformAdmin();

  const ticketsQuery = useQuery({
    queryKey: [...SUPPORT_TICKETS_QUERY_KEY, companyId, isPlatformAdmin],
    queryFn: async () => {
      if (!companyId && !isPlatformAdmin) return [];
      return SupportService.getTickets();
    },
    enabled: !!companyId || isPlatformAdmin,
  });

  const createTicketMutation = useMutation({
    mutationFn: async ({
      title,
      description,
      files,
      onUploadProgress,
    }: {
      title: string;
      description: string;
      files: FileList | File[];
      onUploadProgress?: (uploaded: number, total: number) => void;
    }) => {
      if (!companyId || !user?.id) {
        throw new Error('Kullanıcı veya şirket bilgisi bulunamadı.');
      }

      const ticket = await SupportService.createTicket({
        companyId,
        userId: user.id,
        title,
        description,
      });

      const hasFiles = Array.from(files).length > 0;
      if (hasFiles) {
        await SupportService.uploadAndAttachScreenshots(companyId, ticket.id, files, onUploadProgress);
      }

      return ticket;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: SUPPORT_TICKETS_QUERY_KEY,
      });
    },
  });

  const updateStatusMutation = useMutation({
    mutationFn: async ({ ticketId, status }: { ticketId: string; status: SupportTicketStatus }) => {
      if (!user?.id) {
        throw new Error('Kullanıcı bilgisi bulunamadı.');
      }
      await SupportService.updateTicketStatus(ticketId, status, user.id);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: SUPPORT_TICKETS_QUERY_KEY,
      });
    },
  });

  useEffect(() => {
    if (!companyId && !isPlatformAdmin) return;

    const baseConfig = {
      event: '*' as const,
      schema: 'public' as const,
    };

    const channel = supabase
      .channel(`support-tickets-${companyId || 'platform-admin'}`)
      .on(
        'postgres_changes',
        companyId
          ? { ...baseConfig, table: 'support_tickets', filter: `company_id=eq.${companyId}` }
          : { ...baseConfig, table: 'support_tickets' },
        () => {
          void queryClient.invalidateQueries({
            queryKey: SUPPORT_TICKETS_QUERY_KEY,
          });
        }
      )
      .on(
        'postgres_changes',
        companyId
          ? { ...baseConfig, table: 'support_ticket_attachments', filter: `company_id=eq.${companyId}` }
          : { ...baseConfig, table: 'support_ticket_attachments' },
        () => {
          void queryClient.invalidateQueries({
            queryKey: SUPPORT_TICKETS_QUERY_KEY,
          });
        }
      )
      .subscribe();

    return () => {
      void supabase.removeChannel(channel);
    };
  }, [companyId, isPlatformAdmin, queryClient]);

  return {
    tickets: ticketsQuery.data || [],
    isLoading: ticketsQuery.isLoading,
    error: ticketsQuery.error,
    isPlatformAdmin,
    createTicket: createTicketMutation.mutateAsync,
    isCreatingTicket: createTicketMutation.isPending,
    updateTicketStatus: updateStatusMutation.mutateAsync,
    isUpdatingTicketStatus: updateStatusMutation.isPending,
  };
};
